import { takeReturnTo } from "./lib/returnTo";
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import MainLayout from "./components/MainLayout";
import { useTenant } from "./hooks/useTenant";
import { getAllowedMenuIds, getHomePath } from "./config/tenants";

// Import Pages
import AnalystBroHome from "./pages/AnalystBroHome";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import ChatPage from "./pages/ChatPage";
import HermesPage from "./pages/HermesPage";
import FlowPage from "./pages/FlowPage";
import TechnicalChartPage from "./pages/TechnicalChartPage";
import StockScreenerPage from "./pages/StockScreenerPage";
import ContentMonitoringPage from "./pages/ContentMonitoringPage";
import CommunityPage from "./pages/CommunityPage";
import HistoryPage from "./pages/HistoryPage";
import UpgradePage from "./pages/UpgradePage";
import CheckoutPage from "./pages/CheckoutPage";
import SettingsPage from "./pages/SettingsPage";
import WatchlistPage from "./pages/WatchlistPage";
import DesktopAlertsPage from "./pages/DesktopAlertsPage";
import PortfolioPage from "./pages/PortfolioPage";
import StockPage from "./pages/StockPage";
import MarketDataPage from "./pages/MarketDataPage";
import BacktestingPage from "./pages/BacktestingPage";
import AiAppsPage from "./pages/AiAppsPage";

// Tools & Blog Pages
import BlogPostPage from "./pages/blog/BlogPostPage";

// RequireAuth Component

// /stocks/... (plan 285 v1) -> /symbols/... keeping the sub-path, query and hash.
function LegacyStocksRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/stocks/i, "/symbols")}${search}${hash}`} replace />;
}

// App screens (signed-in tools, login, checkout) are not content: tell search engines not to index them.
// The public pages (home, markets, symbols, backtesting, blog) are left alone.
const PUBLIC_PREFIXES = ["/research", "/markets", "/symbols", "/stocks", "/backtesting", "/blog"];
function RobotsMeta() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    const isPublic = pathname === "/" || PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
    let tag = document.querySelector('meta[name="robots"][data-app]');
    if (isPublic) {
      if (tag) tag.remove();
      return;
    }
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      tag.setAttribute("data-app", "1");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", "noindex, nofollow");
  }, [pathname]);
  return null;
}

function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading AnalystBro...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function AppContent() {
  const { isAuthenticated } = useAuth();
  const { subdomain } = useTenant();
  const allowedIds = getAllowedMenuIds(subdomain);

  const homePath = getHomePath(subdomain);

  // Redirect authenticated users from login to home
  const PublicRoute = ({ children }) => {
    if (isAuthenticated) {
      // Back to where sign-in started (e.g. a stock's reports page), else the app home.
      return <Navigate to={takeReturnTo(homePath)} replace />;
    }
    return children;
  };

  return (
    <Router>
      <RobotsMeta />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            /* Public homepage for everyone; signed-in users see "Open AnalystBro" in the navbar. */
            <AnalystBroHome />
          }
        />
        <Route
          path="/auth"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="/checkout" element={<CheckoutPage />} />

        {/* Server-side allauth Google redirect lands here with the JWT in the
            URL fragment. PUBLIC + not behind PublicRoute so the callback always
            runs and hydrates the session before navigating into the app. */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Standalone demo/recording pages — no layout, no auth */}
        <Route path="/demo-closing" element={<Navigate to="/" replace />} />  {/* old demo page */}

        {/* The old tools pages are gone; their links land on the homepage. */}
        <Route path="/tools/*" element={<Navigate to="/" replace />} />

        {/* Research Blog */}
        {/* The form lives on the homepage; old /contact links land on it. */}
        <Route path="/contact" element={<Navigate to="/#contact" replace />} />
        <Route path="/feedback" element={<Navigate to="/#contact" replace />} />
        {/* Public symbol pages (equities, ETFs): /symbols/EXCHANGE:SYMBOL[/<report-kind>[/<period>]]
            (backend plan 285). The v1 /stocks/... URLs redirect here (Django 301s them too). */}
        <Route path="/symbols" element={<StockPage />} />
        <Route path="/symbols/*" element={<StockPage />} />
        <Route path="/stocks" element={<LegacyStocksRedirect />} />
        <Route path="/stocks/*" element={<LegacyStocksRedirect />} />
        {/* Market data pages (backend plan 300): server-rendered, kept as is by MarketDataPage. */}
        <Route path="/research" element={<MarketDataPage />} />  {/* server-rendered research hub */}
        <Route path="/research/*" element={<MarketDataPage />} />  {/* author pages */}
        <Route path="/markets" element={<MarketDataPage />} />
        <Route path="/markets/*" element={<MarketDataPage />} />
        <Route path="/backtesting" element={<BacktestingPage />} />
        <Route path="/ai-apps" element={<AiAppsPage />} />
        <Route path="/blog" element={<Navigate to="/research" replace />} />  {/* old placeholder page */}
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* AgentBro (brand) — engine is Hermes internally (HermesPage component,
            /api/agent/hermes/session). Public URL is /agentbro. */}
        <Route path="/agentbro" element={<RequireAuth><MainLayout><HermesPage /></MainLayout></RequireAuth>} />
        <Route path="/agentbro/:convId" element={<RequireAuth><MainLayout><HermesPage /></MainLayout></RequireAuth>} />

        {/* Protected Routes */}
        <Route element={<RequireAuth><MainLayout /></RequireAuth>}>
          {/* Always-visible routes (not menu-gated) */}
          <Route path="/upgrade" element={<UpgradePage />} />
          <Route path="/settings" element={<SettingsPage />} />

          {/* Tenant-gated routes */}
          {allowedIds.includes('chat') && <Route path="/chat" element={<ChatPage />} />}
          {allowedIds.includes('chart') && <Route path="/chart" element={<TechnicalChartPage />} />}
          {allowedIds.includes('screener') && <Route path="/screener" element={<StockScreenerPage />} />}
          {allowedIds.includes('history') && <Route path="/history" element={<HistoryPage />} />}
          {allowedIds.includes('watchlist') && <Route path="/watchlist" element={<WatchlistPage />} />}
          {allowedIds.includes('portfolio') && <Route path="/portfolio" element={<PortfolioPage />} />}
          {allowedIds.includes('alerts') && <Route path="/alerts" element={<DesktopAlertsPage />} />}

          {/* Legacy/unused routes — kept registered but not in menu */}
          <Route path="/monitoring" element={<ContentMonitoringPage />} />
          <Route path="/flow" element={<FlowPage />} />
          <Route path="/community" element={<CommunityPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to={isAuthenticated ? homePath : "/"} replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
